import React, { useState, useEffect, useMemo } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { LineChart, Line, AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, BarChart, Bar, Legend } from 'recharts';
import { Activity, TrendingUp, BarChart3 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function fmt(n) {
  if (n === null || n === undefined || !isFinite(n)) return '—';
  if (Math.abs(n) >= 1e6) return (n / 1e6).toFixed(1) + 'M';
  if (Math.abs(n) >= 1e3) return (n / 1e3).toFixed(1) + 'K';
  return n.toFixed(2);
}

function fmtPrice(n) {
  if (!n || !isFinite(n)) return '—';
  if (n < 0.0001) return '$' + n.toExponential(3);
  if (n < 0.01) return '$' + n.toFixed(6);
  return '$' + n.toFixed(4);
}

function CustomTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  const d = payload[0]?.payload;
  if (!d) return null;
  const time = new Date(d.timestamp);
  return (
    <div className="bg-zinc-900 border border-white/10 rounded-lg p-3 text-xs shadow-xl">
      <p className="text-muted-foreground mb-1.5 font-medium">{time.toLocaleString()}</p>
      {d.volume_usd !== undefined && <p className="text-blue-400">Volume: <span className="font-mono font-semibold">${fmt(d.volume_usd)}</span></p>}
      {d.price_usd && <p className="text-amber-400">Price: <span className="font-mono font-semibold">{fmtPrice(d.price_usd)}</span></p>}
      {d.swap_count !== undefined && <p className="text-purple-400">Swaps: <span className="font-mono font-semibold">{d.swap_count}</span></p>}
      {d.buy_count !== undefined && d.sell_count !== undefined && (
        <p className="text-muted-foreground">
          <span className="text-emerald-400">{d.buy_count} buys</span> / <span className="text-red-400">{d.sell_count} sells</span>
        </p>
      )}
    </div>
  );
}

export function LiquidityHistory({ currentPrice = 0 }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState(30);
  const [selectedPool, setSelectedPool] = useState('all');
  const [chartType, setChartType] = useState('volume');

  useEffect(() => {
    async function fetchData() {
      setLoading(true);
      try {
        const url = selectedPool === 'all'
          ? `${API_URL}/api/dex/liquidity-history?days=${period}`
          : `${API_URL}/api/dex/liquidity-history?days=${period}&pool_address=${selectedPool}`;
        const res = await fetch(url);
        const json = await res.json();
        setData(json);
      } catch (err) {
        console.error('Failed to fetch liquidity history:', err);
      }
      setLoading(false);
    }
    fetchData();
  }, [period, selectedPool]);

  const { chartData, poolOptions, summary } = useMemo(() => {
    if (!data?.pools) return { chartData: [], poolOptions: [], summary: null };

    const poolKeys = Object.keys(data.pools);
    const poolOpts = poolKeys.map(addr => ({
      address: addr,
      pair: data.pools[addr].pair || `${addr.slice(0, 6)}...`
    }));

    // Merge all pool datapoints into a single timeline
    const timeMap = {};
    let totalVol = 0, totalSwaps = 0, totalBuys = 0, totalSells = 0;

    for (const [addr, poolData] of Object.entries(data.pools)) {
      const poolPair = poolData.pair || '';
      for (const dp of poolData.datapoints || []) {
        const key = dp.timestamp;
        if (!timeMap[key]) {
          timeMap[key] = {
            timestamp: key,
            volume_usd: 0, swap_count: 0, buy_count: 0, sell_count: 0,
            price_usd: null, volume_krown: 0
          };
        }
        timeMap[key].volume_usd += dp.volume_usd || 0;
        timeMap[key].swap_count += dp.swap_count || 0;
        timeMap[key].buy_count += dp.buy_count || 0;
        timeMap[key].sell_count += dp.sell_count || 0;
        timeMap[key].volume_krown += dp.volume_krown || 0;
        if (dp.price_usd) timeMap[key].price_usd = dp.price_usd;

        // Pool-specific volumes for stacked view
        timeMap[key][`vol_${poolPair}`] = (timeMap[key][`vol_${poolPair}`] || 0) + (dp.volume_usd || 0);
      }

      if (poolData.summary) {
        totalVol += poolData.summary.total_volume_usd || 0;
        totalSwaps += poolData.summary.total_swaps || 0;
        totalBuys += poolData.summary.total_buys || 0;
        totalSells += poolData.summary.total_sells || 0;
      }
    }

    const sorted = Object.values(timeMap).sort((a, b) => a.timestamp.localeCompare(b.timestamp));

    // Forward-fill price for smoother chart
    let lastPrice = null;
    for (const dp of sorted) {
      if (dp.price_usd) lastPrice = dp.price_usd;
      else if (lastPrice) dp.price_usd = lastPrice;
    }

    return {
      chartData: sorted,
      poolOptions: poolOpts,
      summary: { totalVol, totalSwaps, totalBuys, totalSells }
    };
  }, [data]);

  const periods = [
    { label: '24H', value: 1 },
    { label: '7D', value: 7 },
    { label: '30D', value: 30 },
    { label: '90D', value: 90 },
    { label: 'ALL', value: 365 },
  ];

  const chartTypes = [
    { label: 'Volume', value: 'volume', icon: BarChart3 },
    { label: 'Price', value: 'price', icon: TrendingUp },
    { label: 'Activity', value: 'activity', icon: Activity },
  ];

  return (
    <Card className="mt-8" data-testid="liquidity-history">
      <CardHeader>
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <CardTitle className="text-lg flex items-center gap-2">
            <Activity className="w-5 h-5 text-blue-400" />
            Pool Liquidity & Volume History
          </CardTitle>
          <div className="flex flex-wrap gap-2">
            {/* Period selector */}
            <div className="flex rounded-lg overflow-hidden border border-white/10">
              {periods.map(p => (
                <button
                  key={p.value}
                  data-testid={`liq-hist-period-${p.value}`}
                  onClick={() => setPeriod(p.value)}
                  className={`px-2.5 py-1 text-[10px] font-medium transition-colors ${
                    period === p.value ? 'bg-blue-600 text-white' : 'bg-white/5 text-muted-foreground hover:text-white'
                  }`}
                >
                  {p.label}
                </button>
              ))}
            </div>
            {/* Chart type selector */}
            <div className="flex rounded-lg overflow-hidden border border-white/10">
              {chartTypes.map(ct => (
                <button
                  key={ct.value}
                  data-testid={`liq-hist-type-${ct.value}`}
                  onClick={() => setChartType(ct.value)}
                  className={`px-2.5 py-1 text-[10px] font-medium transition-colors flex items-center gap-1 ${
                    chartType === ct.value ? 'bg-blue-600 text-white' : 'bg-white/5 text-muted-foreground hover:text-white'
                  }`}
                >
                  <ct.icon className="w-3 h-3" /> {ct.label}
                </button>
              ))}
            </div>
          </div>
        </div>
        {/* Pool filter */}
        {poolOptions.length > 1 && (
          <div className="flex flex-wrap gap-1.5 mt-2">
            <button
              data-testid="liq-hist-pool-all"
              onClick={() => setSelectedPool('all')}
              className={`px-2 py-1 text-[10px] font-mono rounded-md border transition-all ${
                selectedPool === 'all' ? 'border-blue-500 bg-blue-500/20 text-blue-300' : 'border-white/10 bg-white/5 text-muted-foreground hover:text-white'
              }`}
            >
              All Pools
            </button>
            {poolOptions.map(p => (
              <button
                key={p.address}
                data-testid={`liq-hist-pool-${p.address.slice(0, 8)}`}
                onClick={() => setSelectedPool(p.address)}
                className={`px-2 py-1 text-[10px] font-mono rounded-md border transition-all ${
                  selectedPool === p.address ? 'border-blue-500 bg-blue-500/20 text-blue-300' : 'border-white/10 bg-white/5 text-muted-foreground hover:text-white'
                }`}
              >
                {p.pair}
              </button>
            ))}
          </div>
        )}
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="h-64 flex items-center justify-center text-muted-foreground text-sm">Loading history...</div>
        ) : chartData.length === 0 ? (
          <div className="h-64 flex items-center justify-center text-muted-foreground text-sm">No trading data for this period</div>
        ) : (
          <>
            {/* Summary stats */}
            {summary && (
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
                <div className="bg-white/5 rounded-lg p-3">
                  <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">Total Volume</p>
                  <p className="text-sm font-mono font-semibold text-blue-400">${fmt(summary.totalVol)}</p>
                </div>
                <div className="bg-white/5 rounded-lg p-3">
                  <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">Total Swaps</p>
                  <p className="text-sm font-mono font-semibold">{summary.totalSwaps.toLocaleString()}</p>
                </div>
                <div className="bg-white/5 rounded-lg p-3">
                  <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">Buys</p>
                  <p className="text-sm font-mono font-semibold text-emerald-400">{summary.totalBuys.toLocaleString()}</p>
                </div>
                <div className="bg-white/5 rounded-lg p-3">
                  <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">Sells</p>
                  <p className="text-sm font-mono font-semibold text-red-400">{summary.totalSells.toLocaleString()}</p>
                </div>
              </div>
            )}

            {/* Chart */}
            <div className="h-72" data-testid="liq-hist-chart">
              <ResponsiveContainer width="100%" height="100%">
                {chartType === 'volume' ? (
                  <BarChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                    <XAxis
                      dataKey="timestamp"
                      tick={{ fill: '#71717a', fontSize: 10 }}
                      tickFormatter={(v) => {
                        const d = new Date(v);
                        return period <= 1 ? d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : d.toLocaleDateString([], { month: 'short', day: 'numeric' });
                      }}
                    />
                    <YAxis tick={{ fill: '#71717a', fontSize: 10 }} tickFormatter={(v) => '$' + fmt(v)} />
                    <Tooltip content={<CustomTooltip />} />
                    <Bar dataKey="volume_usd" fill="#3b82f6" radius={[2, 2, 0, 0]} name="Volume USD" />
                  </BarChart>
                ) : chartType === 'price' ? (
                  <AreaChart data={chartData}>
                    <defs>
                      <linearGradient id="priceGradient" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#f59e0b" stopOpacity={0.3} />
                        <stop offset="95%" stopColor="#f59e0b" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                    <XAxis
                      dataKey="timestamp"
                      tick={{ fill: '#71717a', fontSize: 10 }}
                      tickFormatter={(v) => {
                        const d = new Date(v);
                        return period <= 1 ? d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : d.toLocaleDateString([], { month: 'short', day: 'numeric' });
                      }}
                    />
                    <YAxis tick={{ fill: '#71717a', fontSize: 10 }} tickFormatter={(v) => fmtPrice(v)} domain={['dataMin', 'dataMax']} />
                    <Tooltip content={<CustomTooltip />} />
                    <Area type="monotone" dataKey="price_usd" stroke="#f59e0b" fill="url(#priceGradient)" strokeWidth={2} name="Price USD" dot={false} />
                  </AreaChart>
                ) : (
                  <BarChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                    <XAxis
                      dataKey="timestamp"
                      tick={{ fill: '#71717a', fontSize: 10 }}
                      tickFormatter={(v) => {
                        const d = new Date(v);
                        return period <= 1 ? d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : d.toLocaleDateString([], { month: 'short', day: 'numeric' });
                      }}
                    />
                    <YAxis tick={{ fill: '#71717a', fontSize: 10 }} />
                    <Tooltip content={<CustomTooltip />} />
                    <Legend wrapperStyle={{ fontSize: 10 }} />
                    <Bar dataKey="buy_count" fill="#22c55e" stackId="a" radius={[0, 0, 0, 0]} name="Buys" />
                    <Bar dataKey="sell_count" fill="#ef4444" stackId="a" radius={[2, 2, 0, 0]} name="Sells" />
                  </BarChart>
                )}
              </ResponsiveContainer>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}

export default LiquidityHistory;
